import { ModelCampaign } from '../models/campaign/campaign.model';
import { ModelCampaignListDetails } from '../models/campaign/campaign-list-details.model';
import { ModelMenu } from '../models/menu/menu.model';
import { ModelRedirectCampaign } from '../models/campaign/redirect-campaign.model';
import { ModelSubscriptionRecord } from '../models/subscription/subscription-record.model';
import { MenuItemType } from '../enums/standalone/menu-item-type.enum';

/**
 * In-memory seed data for MockServiceAzureStorage.
 *
 * The real ServiceAzureStorage talks to Azure Blob Storage via the @azure/storage-blob SDK
 * (listBlobsByHierarchy / block + append blobs), which json-server cannot represent. So the mock
 * serves this data directly instead. The shapes below were captured from the live dev container
 * (https://staatodev001.blob.core.windows.net/campaigns) so they match production exactly.
 *
 * IMPORTANT: the organization ids here MUST match the `organizations` claim seeded in
 * account-from-b2c.mockdata.json — screens call listCampaignsForOrganization(selectedOrganization.organizationId).
 */

export const MOCK_ORGANIZATION_ID_DEFAULT = 'a4504aa1-11bf-47de-8b69-b2ceec4319a4';
export const MOCK_ORGANIZATION_ID_SECONDARY = 'dd42bbd6-b819-46ee-87ad-c45ccb14b2f7';

const CAMPAIGN_ID_COMPETITION = '20250803-0737-b5505628-f99b-4ab3-9fff-8da9bbda614c';
const CAMPAIGN_ID_SUBSCRIPTION = '20260629-1252-7c44cfaa-1b5e-4e28-a748-8946f305237c';
const CAMPAIGN_ID_REDIRECT = '20260711-1200-c0aeb3cc-c563-432b-a9c9-d9a82780ae98';
const CAMPAIGN_ID_MENU = '20260710-0900-abcd1234-5678-49ab-cdef-000000000001';
const CAMPAIGN_ID_SECONDARY_REDIRECT = '20260711-1134-c0aeb3cc-c563-432b-a9c9-d9a82780ae98';

const STORAGE_BASE = 'https://staatodev001.blob.core.windows.net/campaigns';

/**
 * Campaign lists per organization (what listCampaignsForOrganization / filterBlobsByMetadata return).
 * `viewer` is the MediaTypes value used to route to the correct media viewer.
 */
export const MOCK_CAMPAIGN_LISTS: Record<string, ModelCampaignListDetails[]> = {
  [MOCK_ORGANIZATION_ID_DEFAULT]: [
    {
      id: CAMPAIGN_ID_COMPETITION,
      name: 'Win a Dream Vacation',
      url: `https://dev.aauto.co.za/view/competition/${MOCK_ORGANIZATION_ID_DEFAULT}/${CAMPAIGN_ID_COMPETITION}`,
      description:
        'Enter for a chance to win an all-expenses-paid vacation to the Maldives! Includes flights, accommodation, and activities for two people.',
      fromDate: new Date('2025-08-03T05:37:01.456Z'),
      toDate: new Date('2025-09-02T05:37:01.456Z'),
      viewer: 'competition',
      createdBy: 'Sakkie',
      blobItems: [],
      isArchived: false
    },
    {
      id: CAMPAIGN_ID_SUBSCRIPTION,
      name: 'Castle Gate POC',
      url: `https://dev.aauto.co.za/view/subscription-campaign/${MOCK_ORGANIZATION_ID_DEFAULT}/${CAMPAIGN_ID_SUBSCRIPTION}`,
      description: 'POC for Castle Gate',
      fromDate: new Date('2026-06-29T10:52:17.732Z'),
      toDate: new Date('2026-07-29T10:52:17.732Z'),
      viewer: 'subscription-campaign',
      createdBy: 'Sakkie',
      blobItems: [],
      isArchived: false
    },
    {
      id: CAMPAIGN_ID_REDIRECT,
      name: 'Demo Redirect QR',
      url: `https://dev.aauto.co.za/view/redirect/${MOCK_ORGANIZATION_ID_DEFAULT}/${CAMPAIGN_ID_REDIRECT}`,
      description: 'A re-pointable QR that currently forwards to the Castle Gate POC subscription campaign.',
      fromDate: new Date('2026-07-11T12:00:00.000Z'),
      toDate: new Date('2026-08-10T12:00:00.000Z'),
      viewer: 'redirect',
      createdBy: 'Sakkie',
      blobItems: [],
      isArchived: false
    },
    {
      id: CAMPAIGN_ID_MENU,
      name: 'Demo Restaurant Menu',
      url: `https://dev.aauto.co.za/view/menu/${MOCK_ORGANIZATION_ID_DEFAULT}/${CAMPAIGN_ID_MENU}`,
      description: 'Interactive menu demo for the Aauto café.',
      fromDate: new Date('2026-07-10T09:00:00.000Z'),
      toDate: new Date('2026-12-31T09:00:00.000Z'),
      viewer: 'menu',
      createdBy: 'Sakkie',
      blobItems: [],
      isArchived: false
    }
  ],
  [MOCK_ORGANIZATION_ID_SECONDARY]: [
    {
      id: CAMPAIGN_ID_SECONDARY_REDIRECT,
      name: 'Castle Gate QR',
      url: `http://localhost:4200/view/redirect/${MOCK_ORGANIZATION_ID_SECONDARY}/${CAMPAIGN_ID_SECONDARY_REDIRECT}`,
      description: 'QR for castle gate design campaign',
      fromDate: new Date('2026-07-11T09:34:10.939Z'),
      toDate: new Date('2026-08-10T09:34:10.939Z'),
      viewer: 'redirect',
      createdBy: 'Sakkie',
      blobItems: [],
      isArchived: false
    }
  ]
};

/**
 * Per-blob file contents, keyed by the relative blob path `${organizationId}/${campaignId}/${fileName}`.
 * loadFile / loadCampaignFile / loadMenuFile / loadRedirectCampaignFile all resolve against this map.
 */
export const MOCK_BLOB_FILES: Record<string, unknown> = {
  // --- Competition campaign ---
  [`${MOCK_ORGANIZATION_ID_DEFAULT}/${CAMPAIGN_ID_COMPETITION}/campaign.json`]: {
    id: CAMPAIGN_ID_COMPETITION,
    description:
      'Enter for a chance to win an all-expenses-paid vacation to the Maldives! Includes flights, accommodation, and activities for two people.',
    endDate: '2025-09-02T05:37:01.456Z',
    hasExpirationDate: false,
    isEmbedded: false,
    mediaType: {
      name: 'Competition',
      value: 'competition',
      iconName: 'competition-logo.svg',
      iconLocation: 'assets/logos_viewer/competition-logo.svg',
      helpDescription:
        'Link your QR code to a competition. The user will be able to enter the competition and view the details of the competition.',
      serviceName: 'CampaignServiceCompetition'
    },
    name: 'Win a Dream Vacation',
    qrCodeDetails: {
      foreGroundColor: '#000000',
      backGroundColor: '#FFFFFF',
      content: `https://dev.aauto.co.za/view/competition/${MOCK_ORGANIZATION_ID_DEFAULT}/${CAMPAIGN_ID_COMPETITION}?scan=true`
    },
    startDate: '2025-08-03T05:37:01.456Z',
    url: `https://dev.aauto.co.za/view/competition/${MOCK_ORGANIZATION_ID_DEFAULT}/${CAMPAIGN_ID_COMPETITION}`
  } as unknown as ModelCampaign,
  [`${MOCK_ORGANIZATION_ID_DEFAULT}/${CAMPAIGN_ID_COMPETITION}/competition.json`]: {
    geolocationRequest: 'Share your location?',
    isGeolocationEnabled: true,
    isNotificationsEnabled: true,
    isTrackingEnabled: true,
    notificationRequest: 'Would you like to receive notifications',
    competitionName: 'Win a Dream Vacation',
    description:
      'Enter for a chance to win an all-expenses-paid vacation to the Maldives! Includes flights, accommodation, and activities for two people.',
    location: 'Cape Town, South Africa',
    startDate: '2025-08-03T05:36:39.453Z',
    endDate: '2025-10-03T05:36:39.453Z',
    imageOption: 'upload',
    uploadImage: `${STORAGE_BASE}/${MOCK_ORGANIZATION_ID_DEFAULT}/${CAMPAIGN_ID_COMPETITION}/branding/competition-banner.webp`,
    entryRequirements: {
      name: true,
      surname: true,
      mobileNumber: true,
      emailAddress: true,
      dateOfBirth: false,
      idNumber: false
    },
    isProofOfEntryEnabled: true,
    proofOfEntryRequest: 'Upload proof of purchase',
    termsOption: 'pdf',
    termsUrl: `${STORAGE_BASE}/${MOCK_ORGANIZATION_ID_DEFAULT}/${CAMPAIGN_ID_COMPETITION}/terms-and-conditions.pdf`
  },

  // --- Subscription-for-notifications campaign ---
  [`${MOCK_ORGANIZATION_ID_DEFAULT}/${CAMPAIGN_ID_SUBSCRIPTION}/campaign.json`]: {
    id: CAMPAIGN_ID_SUBSCRIPTION,
    description: 'POC for Castle Gate',
    endDate: '2026-07-29T10:52:17.732Z',
    hasExpirationDate: false,
    isEmbedded: false,
    mediaType: {
      name: 'Subscription for notifications',
      value: 'subscription-campaign',
      iconName: 'competition-logo.svg',
      iconLocation: 'assets/logos_viewer/competition-logo.svg',
      helpDescription:
        'Link your QR code to a information page. The user will be able to enter the personal details if required and opt in to receive notifications.',
      serviceName: 'CampaignServiceSubscriptionForNotifications'
    },
    name: 'Castle Gate POC',
    qrCodeDetails: {
      foreGroundColor: '#000000',
      backGroundColor: '#FFFFFF',
      content: `https://dev.aauto.co.za/view/subscription-campaign/${MOCK_ORGANIZATION_ID_DEFAULT}/${CAMPAIGN_ID_SUBSCRIPTION}?scan=true`
    },
    startDate: '2026-06-29T10:52:17.732Z',
    url: `https://dev.aauto.co.za/view/subscription-campaign/${MOCK_ORGANIZATION_ID_DEFAULT}/${CAMPAIGN_ID_SUBSCRIPTION}`
  } as unknown as ModelCampaign,
  [`${MOCK_ORGANIZATION_ID_DEFAULT}/${CAMPAIGN_ID_SUBSCRIPTION}/subscription-campaign.json`]: {
    geolocationRequest: '',
    isGeolocationEnabled: false,
    isNotificationsEnabled: false,
    isTrackingEnabled: true,
    notificationRequest: '',
    subscriptionName: 'Castle Gate POC',
    description: '',
    startDate: '2026-06-29T10:50:18.449Z',
    endDate: '2026-07-29T10:50:18.449Z',
    imageOption: 'upload',
    uploadImage: `${STORAGE_BASE}/${MOCK_ORGANIZATION_ID_DEFAULT}/${CAMPAIGN_ID_SUBSCRIPTION}/branding/subscription-banner.webp`,
    entryRequirements: {
      name: true,
      surname: false,
      mobileNumber: true,
      emailAddress: true,
      dateOfBirth: false,
      idNumber: false
    }
  },

  // --- Redirect campaign (default org) ---
  [`${MOCK_ORGANIZATION_ID_DEFAULT}/${CAMPAIGN_ID_REDIRECT}/campaign.json`]: {
    id: CAMPAIGN_ID_REDIRECT,
    description: 'A re-pointable QR that currently forwards to the Castle Gate POC subscription campaign.',
    endDate: '2026-08-10T12:00:00.000Z',
    hasExpirationDate: false,
    isEmbedded: false,
    mediaType: {
      name: 'Redirect',
      value: 'redirect',
      iconName: 'webpage-logo.svg',
      iconLocation: 'assets/logos_viewer/webpage-logo.svg',
      helpDescription:
        'Link your QR code to one of your other campaigns or a custom URL. The QR code never changes — you can re-point the destination at any time without reprinting.',
      serviceName: 'CampaignServiceRedirect'
    },
    name: 'Demo Redirect QR',
    qrCodeDetails: {
      foreGroundColor: '#000000',
      backGroundColor: '#FFFFFF',
      content: `https://dev.aauto.co.za/view/redirect/${MOCK_ORGANIZATION_ID_DEFAULT}/${CAMPAIGN_ID_REDIRECT}?scan=true`
    },
    startDate: '2026-07-11T12:00:00.000Z',
    url: `https://dev.aauto.co.za/view/redirect/${MOCK_ORGANIZATION_ID_DEFAULT}/${CAMPAIGN_ID_REDIRECT}`
  } as unknown as ModelCampaign,
  [`${MOCK_ORGANIZATION_ID_DEFAULT}/${CAMPAIGN_ID_REDIRECT}/redirect-campaign.json`]: {
    targetType: 'campaign',
    targetCampaignId: CAMPAIGN_ID_SUBSCRIPTION,
    targetCampaignName: 'Castle Gate POC',
    targetViewer: 'subscription-campaign',
    customUrl: '',
    history: [
      {
        changedAtUtc: '2026-07-11T12:00:00.000Z',
        changedBy: 'Sakkie',
        targetType: 'campaign',
        targetCampaignId: CAMPAIGN_ID_SUBSCRIPTION,
        targetCampaignName: 'Castle Gate POC',
        targetViewer: 'subscription-campaign',
        customUrl: ''
      }
    ]
  } as ModelRedirectCampaign,

  // --- Interactive menu campaign ---
  [`${MOCK_ORGANIZATION_ID_DEFAULT}/${CAMPAIGN_ID_MENU}/campaign.json`]: {
    id: CAMPAIGN_ID_MENU,
    description: 'Interactive menu demo for the Aauto café.',
    endDate: '2026-12-31T09:00:00.000Z',
    hasExpirationDate: false,
    isEmbedded: false,
    mediaType: {
      name: 'Interactive Menu',
      value: 'menu',
      iconName: 'menu-logo.svg',
      iconLocation: 'assets/logos_viewer/menu-logo.svg',
      helpDescription: 'Link your QR code to an interactive restaurant menu.',
      serviceName: 'CampaignServiceInteractiveMenu'
    },
    name: 'Demo Restaurant Menu',
    qrCodeDetails: {
      foreGroundColor: '#000000',
      backGroundColor: '#FFFFFF',
      content: `https://dev.aauto.co.za/view/menu/${MOCK_ORGANIZATION_ID_DEFAULT}/${CAMPAIGN_ID_MENU}?scan=true`
    },
    startDate: '2026-07-10T09:00:00.000Z',
    url: `https://dev.aauto.co.za/view/menu/${MOCK_ORGANIZATION_ID_DEFAULT}/${CAMPAIGN_ID_MENU}`
  } as unknown as ModelCampaign,
  [`${MOCK_ORGANIZATION_ID_DEFAULT}/${CAMPAIGN_ID_MENU}/menu.json`]: {
    allowOrders: false,
    cultureCode: 'en-ZA',
    description: 'Freshly brewed coffee and light meals.',
    dishes: [],
    extras: [],
    ingredients: [],
    items: [
      {
        description: 'Coffee, tea and more',
        dishes: [
          {
            calories: 120,
            description: 'Double shot espresso with steamed milk',
            dishId: 101,
            extras: [],
            extrasDescription: '',
            icon: '',
            iconDescription: '',
            imageOrientation: 'landscape',
            imageUrl: '',
            ingredients: [],
            isSpecial: false,
            name: 'Cappuccino',
            options: [],
            price: 32,
            recommendedItems: [],
            removableIngredients: [],
            sortOrder: 1,
            status: 'published',
            tags: ['hot', 'coffee']
          },
          {
            calories: 15,
            description: 'Espresso with hot water',
            dishId: 102,
            extras: [],
            extrasDescription: '',
            icon: '',
            iconDescription: '',
            imageOrientation: 'landscape',
            imageUrl: '',
            ingredients: [],
            isSpecial: false,
            name: 'Americano',
            options: [],
            price: 28,
            recommendedItems: [],
            removableIngredients: [],
            sortOrder: 2,
            status: 'published',
            tags: ['hot', 'coffee']
          }
        ],
        icon: '',
        imageOrientation: 'landscape',
        imageUrl: '',
        isExpanded: true,
        items: [],
        menuItemId: 1,
        name: 'Hot Drinks',
        sortOrder: 1,
        status: 'published',
        type: MenuItemType.Category
      },
      {
        description: 'Something to eat',
        dishes: [
          {
            calories: 350,
            description: 'Cheese and tomato on sourdough',
            dishId: 201,
            extras: [],
            extrasDescription: '',
            icon: '',
            iconDescription: '',
            imageOrientation: 'landscape',
            imageUrl: '',
            ingredients: [],
            isSpecial: false,
            name: 'Toasted Sandwich',
            options: [],
            price: 55,
            recommendedItems: [],
            removableIngredients: [],
            sortOrder: 1,
            status: 'published',
            tags: ['food']
          }
        ],
        icon: '',
        imageOrientation: 'landscape',
        imageUrl: '',
        isExpanded: false,
        items: [],
        menuItemId: 2,
        name: 'Light Meals',
        sortOrder: 2,
        status: 'published',
        type: MenuItemType.Category
      }
    ],
    menuId: CAMPAIGN_ID_MENU,
    menuName: 'Aauto Café Menu',
    options: [],
    restaurantName: 'Aauto Café',
    status: 'published'
  } as ModelMenu,

  // --- Redirect campaign (secondary org) — real dev data ---
  [`${MOCK_ORGANIZATION_ID_SECONDARY}/${CAMPAIGN_ID_SECONDARY_REDIRECT}/campaign.json`]: {
    id: CAMPAIGN_ID_SECONDARY_REDIRECT,
    description: 'QR for castle gate design campaign',
    endDate: '2026-08-10T09:34:10.939Z',
    hasExpirationDate: false,
    isEmbedded: false,
    mediaType: {
      name: 'Redirect',
      value: 'redirect',
      iconName: 'webpage-logo.svg',
      iconLocation: 'assets/logos_viewer/webpage-logo.svg',
      helpDescription:
        'Link your QR code to one of your other campaigns or a custom URL. The QR code never changes — you can re-point the destination at any time without reprinting.',
      serviceName: 'CampaignServiceRedirect'
    },
    name: 'Castle Gate QR',
    qrCodeDetails: {
      foreGroundColor: '#000000',
      backGroundColor: '#FFFFFF',
      content: `http://localhost:4200/view/redirect/${MOCK_ORGANIZATION_ID_SECONDARY}/${CAMPAIGN_ID_SECONDARY_REDIRECT}?scan=true`
    },
    startDate: '2026-07-11T09:34:10.939Z',
    url: `http://localhost:4200/view/redirect/${MOCK_ORGANIZATION_ID_SECONDARY}/${CAMPAIGN_ID_SECONDARY_REDIRECT}`
  } as unknown as ModelCampaign,
  [`${MOCK_ORGANIZATION_ID_SECONDARY}/${CAMPAIGN_ID_SECONDARY_REDIRECT}/redirect-campaign.json`]: {
    targetType: 'campaign',
    targetCampaignId: '20260709-0522-eceda089-3ac6-4347-baff-01921724955d',
    targetCampaignName: 'Castle Gate POC V3',
    targetViewer: 'subscription-campaign',
    customUrl: '',
    history: [
      {
        changedAtUtc: '2026-07-11T09:34:10.939Z',
        changedBy: 'Sakkie',
        targetType: 'campaign',
        targetCampaignId: '20260709-0522-eceda089-3ac6-4347-baff-01921724955d',
        targetCampaignName: 'Castle Gate POC V3',
        targetViewer: 'subscription-campaign',
        customUrl: ''
      }
    ]
  } as ModelRedirectCampaign
};

/**
 * Campaign subscriber lists, keyed by `${organizationId}/${campaignId}`.
 * Returned by loadCampaignSubscriptionFile.
 */
export const MOCK_SUBSCRIPTION_RECORDS: Record<string, ModelSubscriptionRecord[]> = {
  [`${MOCK_ORGANIZATION_ID_DEFAULT}/${CAMPAIGN_ID_SUBSCRIPTION}`]: [
    {
      subscriberId: '20260701-0800-11111111-1111-4111-8111-111111111111',
      organizationId: MOCK_ORGANIZATION_ID_DEFAULT,
      campaignId: CAMPAIGN_ID_SUBSCRIPTION,
      createdAtUtc: '2026-07-01T08:00:00.000Z',
      topicIds: ['topic-promotions'],
      topicsSnapshot: [{ id: 'topic-promotions', name: 'Promotions' }],
      pushSubscription: {
        endpoint: 'https://fcm.googleapis.com/fcm/send/MOCK-ENDPOINT-1',
        expirationTime: null,
        keys: {
          p256dh: 'BIecJM8x9fzA63ujoePhMcLA5051PuOPz0G10UBr-7_OWLKKWUhDJ3eqjybuLT6un96VXN1H2suutW8qXgOzX3g',
          auth: '0Bh0sSn2Aqyrh2I0Ay9o_A'
        }
      } as unknown as PushSubscription
    }
  ]
};
